import { AnimatePresence } from "motion/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import type { AdoptionRateDetailsDto } from "@/api/types";
import { Button } from "@/components/Button/Button";
import { ArrowNarrowDownIcon } from "@/components/Icons/ArrowNarrowDownIcon";
import { ArrowNarrowRightIcon } from "@/components/Icons/ArrowNarrowRightIcon";
import { ArrowUpIcon } from "@/components/Icons/ArrowUpIcon";
import { Label } from "@/components/Label/Label";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetStates } from "./AnalyticsWidgetStates";
import { getProjectPeriodLabel } from "./helpers";

interface WidgetCardProps {
  title: string;
  description?: string;
  href?: string;
  children: React.ReactNode;
  isLoading: boolean;
  isError: boolean;
  isDataAvailable: boolean;
  error: Error | null;
  onReload?: () => void;
  shouldFillHeight?: boolean;
  isHighlighted?: boolean;
}

function WidgetCardRoot({
  title,
  description,
  href,
  children,
  isLoading,
  isError,
  isDataAvailable,
  error,
  onReload,
  shouldFillHeight = true,
  isHighlighted = false,
}: WidgetCardProps): React.ReactNode {
  const { t } = useTranslation();

  const listTitleWords = title.split(" ");

  return (
    <div
      className={twJoin(
        "relative flex w-full flex-col items-center gap-4 rounded-lg bg-white p-4 xl:p-6",
        shouldFillHeight ? "h-full" : "h-fit",
      )}
    >
      {/* Title & Description */}
      <section className="flex w-full items-start justify-between gap-2">
        <div className="flex flex-col">
          <div className="relative w-full">
            <h3 className="text-headline3 xl:text-headline2">
              {/* This is needed to make sure the highlight indicator follows the last word of the title */}
              {listTitleWords.map((word, index) => {
                const isLastWord = index === listTitleWords.length - 1;
                const formattedWord = `${word}${isLastWord ? "" : " "}`;

                return (
                  <span className="relative" key={word}>
                    <span>{formattedWord}</span>
                    {isHighlighted && isLastWord && (
                      <span className="absolute -right-2 top-0 size-2 translate-x-full rounded-full bg-red-500" />
                    )}
                  </span>
                );
              })}
            </h3>
          </div>
          {description && <p className="text-body text-grey-700">{description}</p>}
        </div>
        {href && (
          <Button type="link" styling="ghostPrimary" href={href}>
            {t("component.analytics-widget.adoption.source.btn")}
          </Button>
        )}
      </section>
      <div className="relative size-full">
        <div className={twJoin("flex size-full flex-col gap-6 transition-opacity", !isDataAvailable && "opacity-0")}>
          {children}
        </div>
        {/* Error & Empty states should co-exist with the title */}
        <AnimatePresence>
          {isError && <AnalyticsWidgetStates.Error {...{ error, onReload }} />}
          {!isLoading && !isError && !isDataAvailable && <AnalyticsWidgetStates.Empty />}
        </AnimatePresence>
      </div>
      {/* Loading state should be overlaying the entire widget */}
      <AnimatePresence>{isLoading && <AnalyticsWidgetStates.Loading />}</AnimatePresence>
    </div>
  );
}

function WidgetCardBody({ children }: { children: React.ReactNode }): React.ReactNode {
  return <div className="relative flex w-full flex-col items-center justify-center gap-4">{children}</div>;
}

function WidgetCardFooter({ children }: { children: React.ReactNode }): React.ReactNode {
  return <div className="mt-auto flex w-full flex-col items-center gap-6">{children}</div>;
}

type WidgetReferenceLabelProps = {
  currValue: number;
  referenceValue: number;
};

export const WidgetReferenceLabel = ({ currValue, referenceValue }: WidgetReferenceLabelProps): React.ReactNode => {
  const { t } = useTranslation();

  const difference = currValue - referenceValue;
  let label = t("component.analytics-widget.adoption.trend.neutral.description");
  let icon = <ArrowNarrowRightIcon />;
  let theme: "green" | "yellow" | "blue" = "blue";
  if (difference > 0) {
    icon = <ArrowUpIcon />;
    theme = "green";
    label = t("component.analytics-widget.adoption.trend.positive.description", {
      amount: `${difference.toFixed(1)}%`,
    });
  } else if (difference < 0) {
    icon = <ArrowNarrowDownIcon />;
    theme = "yellow";
    label = t("component.analytics-widget.adoption.trend.negative.description", {
      amount: `${difference.toFixed(1)}%`,
    });
  }

  return (
    <Label size="lg" isUppercase={false} icon={icon} {...{ theme }}>
      {label}
    </Label>
  );
};

type WidgetCardBenchmarkInfoProps = {
  benchmark: number;
  period: AdoptionRateDetailsDto["period"];
};

const WidgetCardBenchmarkInfo = ({ benchmark, period }: WidgetCardBenchmarkInfoProps): React.ReactNode => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col">
      <h4 className="text-headline4">
        {t("component.analytics-widget.common.aop-benchmark.label", {
          score: `${benchmark}%`,
        })}
      </h4>
      <p className="text-body text-grey-700">
        {t("component.analytics-widget.adoption.description", {
          period: getProjectPeriodLabel(t, period),
        })}
      </p>
    </div>
  );
};

export const AnalyticsWidgetCard = {
  Root: WidgetCardRoot,
  Body: WidgetCardBody,
  Footer: WidgetCardFooter,
  ReferenceLabel: WidgetReferenceLabel,
  BenchmarkInfo: WidgetCardBenchmarkInfo,
};
